import { View } from "react-native";
import { BrownButton, TextActionButton } from "@louise/moon-ui";
import { colors } from "@louise/moon-ui/theme/tokens";
import type { InsuranceInfo } from "../types";
import type { VerificationStatus } from "../../../../../../packages/coverage/hooks/useCoverage";
import { useTranslations } from "@src/packages/i18n";
import CoverageCardSurface from "./CoverageCardSurface";
import TexturedCoverageCard from "./TexturedCoverageCard";

type Props = {
  insuranceInfo: InsuranceInfo;
  /** A policy can be present even when a payer name was not returned. */
  hasInsurance?: boolean;
  verificationStatus?: VerificationStatus;
  isActive?: boolean;
  loading?: boolean;
  /** The medical-history treatment is used in Coverage & Benefits. */
  visualStyle?: "flat" | "medical-history";
  onAdd?: () => void;
  onEdit?: () => void;
};

function maskMemberId(memberId: string | null) {
  if (!memberId) return "—";
  return `•••• ${memberId.slice(-4)}`;
}

export default function InsuranceCard({
  insuranceInfo,
  hasInsurance: hasInsuranceProp,
  verificationStatus = "unverified",
  isActive = true,
  loading = false,
  visualStyle = "flat",
  onAdd,
  onEdit,
}: Props) {
  const t = useTranslations();

  if (loading) {
    const loadingFields: [
      { label: string; value: string },
      { label: string; value: string },
    ] = [
      { label: t("you.insurance_card.member_id"), value: "—" },
      { label: t("you.insurance_card.plan"), value: "—" },
    ];

    return (
      <View style={{ marginBottom: 24 }}>
        {visualStyle === "medical-history" ? (
          <TexturedCoverageCard
            category={t("you.insurance_card.insurance")}
            status={t("you.insurance_card.loading")}
            title={t("you.insurance_card.loading_title")}
            subtitle={t("you.insurance_card.loading_subtitle")}
            fields={loadingFields}
            loading
          />
        ) : (
          <CoverageCardSurface
            category={t("you.insurance_card.insurance")}
            status={t("you.insurance_card.loading")}
            title={t("you.insurance_card.loading_title")}
            subtitle={t("you.insurance_card.loading_subtitle")}
            fields={loadingFields}
          />
        )}
      </View>
    );
  }

  const hasInsurance =
    hasInsuranceProp ??
    Boolean(
      insuranceInfo.provider ??
        insuranceInfo.planName ??
        insuranceInfo.memberId,
    );
  const status = !hasInsurance
    ? t("you.insurance_card.not_on_file")
    : isActive
      ? t("you.insurance_card.active")
      : t("you.insurance_card.expired");
  const verificationLabel = t(
    `you.insurance_card.verification_${verificationStatus}`,
  );
  const title = hasInsurance
    ? (insuranceInfo.provider ?? t("you.insurance_card.insurance"))
    : t("you.insurance_card.empty_title");
  const subtitle = hasInsurance
    ? (insuranceInfo.planName ?? t("you.insurance_card.plan_not_added"))
    : t("you.insurance_card.empty_subtitle");
  const fields: [
    { label: string; value: string },
    { label: string; value: string },
  ] = hasInsurance
    ? [
        {
          label: t("you.insurance_card.member_id"),
          value: maskMemberId(insuranceInfo.memberId),
        },
        {
          label: t("you.insurance_card.verification"),
          value: verificationLabel,
        },
      ]
    : [
        {
          label: t("you.insurance_card.member_id"),
          value: t("you.insurance_card.not_added"),
        },
        {
          label: t("you.insurance_card.plan"),
          value: t("you.insurance_card.not_added"),
        },
      ];
  const actionLabel = hasInsurance
    ? t("you.insurance_card.edit")
    : t("you.insurance_card.add_action");
  const onAction = hasInsurance ? onEdit : onAdd;

  return (
    <View style={{ marginBottom: 24, gap: 8 }}>
      {visualStyle === "medical-history" ? (
        <TexturedCoverageCard
          category={t("you.insurance_card.insurance")}
          status={status}
          title={title}
          subtitle={subtitle}
          fields={fields}
          actionLabel={actionLabel}
          onAction={onAction}
        />
      ) : (
        <>
          <CoverageCardSurface
            category={t("you.insurance_card.insurance")}
            status={status}
            title={title}
            subtitle={subtitle}
            fields={fields}
          />

          {hasInsurance && onEdit ? (
            <TextActionButton
              label={t("you.insurance_card.edit")}
              onPress={onEdit}
              color={colors.warm}
              style={{ alignSelf: "flex-start" }}
            />
          ) : onAdd ? (
            <BrownButton
              label={t("you.insurance_card.add_action")}
              onPress={onAdd}
            />
          ) : null}
        </>
      )}
    </View>
  );
}
